import React, { useState, useEffect, useMemo } from "react";
import { Col } from "reactstrap";
import { Toast, getFormattedOptions } from "utility/helpers";
import _ from "lodash";
import {
  fetchAllAgencyProjects,
  fetchMunicipalities,
  fetchProvinces,
} from "services/services.projects";
import "react-datepicker/dist/react-datepicker.css";
import { fetchAllRegions } from "services/services.congressman";
import { congressmanSearch } from "services/services.navbar";
import { officialSearch } from "services/services.official";
import { ACCOUNT_CONGRESSMAN, BEFORE_TWENTIETH } from "utility/constants";
import { useLocation, useNavigate } from "react-router";
import { genericGet, genericPost, genericPut } from "services/services.general";
import styled from "styled-components";
import { HeaderText } from "assets/js/Styles";
import useAgencies from "hooks/useAgencies";
import { AGENCY_TESDA_ID } from "constants/agency";
import { ADD_ACTION } from "constants/actions";
import TVIEndorsementForm from "./Form";
import { TVI_SETTINGS } from "./constants";
import { getHeaderText, validateTVIEndorsementForm } from "./utility";

const HeaderTextSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;
  margin-top: 24px;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 4rem;
  }
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const MainContainer = styled.div`
  margin-top: 4rem;
`;

const AddEndorsementComponent = () => {
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [regionOptions, setRegionOptions] = React.useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedProponent, setSelectedProponent] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [coursesOptions, setCoursesOptions] = useState([]);
  const [isLegacyData, setIsLegacyData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [selectedTVI, setSelectedTVI] = useState(null);

  const { nameOptions: agencies } = useAgencies();

  const defaultAgency = useMemo(
    () => agencies?.find((option) => option.id === AGENCY_TESDA_ID),
    [agencies]
  );

  const [form, setForm] = useState({
    agencyId: null,
    projectId: null,
    provinceId: null,
    regionId: null,
    municipalityId: null,
    tviId: null,
    proponentType: null,
    officialId: null,
    budget: 10,
    implementationDate: null,
    documents: [],
    justification: null,
    editorNotes: null,
    courses: [],
    id: null,
  });

  const location = useLocation();

  const navigate = useNavigate();
  const row = location.state?.row || null;
  const { action } = location.state;

  const fetchProjects = () => {
    fetchAllAgencyProjects({
      url: `/agency/${defaultAgency?.value}`,
      callback: (data) => {
        let projects = data?.Projects;

        if (defaultAgency?.value === 14) {
          projects = data?.Projects.filter(
            (project) => project.name === "Infrastructure"
          );
        }

        const options = getFormattedOptions("name", projects);
        if (defaultAgency?.value === 14) setSelectedProject(options[0]);
        setProjectOptions(options);

        const option = options?.find((op) => op.value === row?.projectId);

        if (option) {
          setSelectedProject(option);
        }

        setIsLoading(false);
      },
    });
  };

  const saveData = (formData) => {
    genericPost({
      url: "/project/assign-congressmen",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          navigate("/settings/tesda");

          Toast({
            type: 1,
            content: "Successfully created course.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const updateData = (formData, endorsementId) => {
    genericPut({
      url: `/recipient/${endorsementId}`,
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated endorsement.",
            });
          }, 1500);

          navigate("/settings/tesda");
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const handleSaveForm = (values) => {
    const isValid = validateTVIEndorsementForm({
      action,
      selectedCourses,
      uploadedFiles,
      coursesOptions,
    });

    if (typeof isValid === "boolean") {
      setIsLoading(true);

      if (row === null) {
        const formData = {
          projectId: values.projectId?.value,
          congressmen: [
            {
              budget: values.budget,
              agencyId: defaultAgency.value,
              programId: values.projectId?.value,
              regionId: values.regionId?.value,
              provinceId: values.provinceId?.value,
              municipalityId:
                values.municipalityId?.value === 0
                  ? null
                  : values.municipalityId?.value,
              tviId: values.tviId?.value,
              officialId:
                values.officialId?.value !== 0
                  ? values.officialId?.value
                  : null,
              id: values.id?.value !== 0 ? values.id?.value : null,
              implementationDate: values.implementationDate,
              documents: {
                supporting: uploadedFiles?.map((file) => file.location),
              },
              justification: values.justification,
              editorNotes:
                values.editorNotes !== ""
                  ? values.editorNotes
                      ?.map((note) => note?.value?.trim())
                      .join(";")
                  : "",
              courses: selectedCourses,
              proponentType: values.proponentType?.label,
              tviEndorsedVia: TVI_SETTINGS,
            },
          ],
        };

        saveData(formData);
      } else {
        const formData = {
          id: row.id,
          courses: selectedCourses.map((item) =>
            _.omit(item, ["course", "availableSlots", "tviCourseId"])
          ),
        };

        updateData(formData, row.id);
      }
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }

    return isValid;
  };

  const handleSearch = _.debounce((inputValue, callback) => {
    if (selectedPosition.label === ACCOUNT_CONGRESSMAN) {
      congressmanSearch({
        inputValue,
        callback,
        isLegacyData,
      });
    } else {
      officialSearch({
        position: selectedPosition.label,
        inputValue,
        callback,
      });
    }
  }, 1000);

  const handleUploadedFiles = (link) => {
    setUploadedFiles((prevUploadedFiles) => {
      const fileIndex = prevUploadedFiles.findIndex(
        (file) => file.name === link.name
      );

      if (fileIndex !== -1) {
        const updatedFiles = [...prevUploadedFiles];
        updatedFiles[fileIndex] = link;

        return updatedFiles;
      }

      if (prevUploadedFiles.length < 5) {
        return [...prevUploadedFiles, link];
      }

      Toast({
        type: 4,
        content: "Exceeded file upload limit of 5",
      });

      return prevUploadedFiles;
    });
  };

  const handleDeleteFile = (file) => {
    const newUploadedFiles = uploadedFiles.filter(
      (files) => files.name !== file
    );
    setUploadedFiles(newUploadedFiles);
  };

  const fetchRegion = () => {
    fetchAllRegions({
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter((region) => region.name !== "Partylist")
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          setRegionOptions(options);
        }
      },
    });
  };

  const handleTviSearch = _.debounce((inputValue, callback) => {
    genericGet({
      url: "/tvi",
      params: {
        provinceId: form?.provinceId?.value,
        search: inputValue,
      },
      callback: (data) => {
        if (data.rows?.length) {
          const options = data.rows?.map((item) => ({
            ...item,
            label: item?.name,
            value: item?.id,
          }));

          callback(options);
        } else {
          callback([]);
        }
      },
    });
  }, 1000);

  const fetchAllProvinces = () => {
    fetchProvinces({
      param: form?.regionId?.value,
      callback: (data) => {
        if (data.length) {
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          setProvinceOptions(options);

          setForm((prevForm) => ({
            ...prevForm,
            municipalityId: null,
            provinceId: options[0],
          }));
        } else {
          setProvinceOptions([]);
        }
      },
    });
  };

  const fetchAllMunicipalities = () => {
    fetchMunicipalities({
      param: form?.provinceId?.value,
      callback: (data) => {
        if (data?.length) {
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          setMunicipalityOptions(options);
        } else {
          setMunicipalityOptions([]);
        }
      },
    });
  };

  const fetchCourses = () => {
    genericGet({
      url: `tvi/${selectedTVI?.value}/courses`,
      callback: (data) => {
        const options = data.rows.map(
          ({ id, course, totalCost, availableSlots }) => ({
            tviCourseId: id,
            course,
            totalCost,
            availableSlots,
          })
        );

        setCoursesOptions(options);
      },
    });
  };

  useEffect(() => {
    fetchRegion();

    if (new Date(row?.createdAt) < BEFORE_TWENTIETH) {
      setIsLegacyData(true);
    }
  }, []);

  useEffect(() => {
    if (form?.provinceId?.value) {
      fetchAllMunicipalities();
    }
  }, [form?.provinceId]);

  useEffect(() => {
    if (form?.regionId?.value) {
      fetchAllProvinces();
    }
  }, [form?.regionId]);

  useEffect(() => {
    if (selectedTVI && action === ADD_ACTION) {
      fetchCourses();
    }
  }, [selectedTVI]);

  useEffect(() => {
    if (defaultAgency) {
      fetchProjects();
    }
  }, [defaultAgency]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getHeaderText(action)}</HeaderText>
        </HeaderTextSection>
        <MainContainer>
          <TVIEndorsementForm
            form={form}
            setForm={setForm}
            handleProponentSearch={handleSearch}
            handleUploadedFiles={handleUploadedFiles}
            handleDeleteFile={handleDeleteFile}
            setUploadedFiles={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            regionOptions={regionOptions}
            setSelectedPosition={setSelectedPosition}
            selectedPosition={selectedPosition}
            setSelectedRegion={setSelectedRegion}
            selectedRegion={selectedRegion}
            setSelectedProponent={setSelectedProponent}
            selectedProponent={selectedProponent}
            setSelectedCourses={setSelectedCourses}
            selectedCourses={selectedCourses}
            coursesOptions={coursesOptions}
            setCoursesOptions={setCoursesOptions}
            row={row}
            action={action}
            projectOptions={projectOptions}
            setSelectedProject={setSelectedProject}
            selectedProject={selectedProject}
            isLoading={isLoading}
            handleSaveForm={handleSaveForm}
            handleTviSearch={handleTviSearch}
            setSelectedTVI={setSelectedTVI}
            selectedTVI={selectedTVI}
            setSelectedProvince={setSelectedProvince}
            selectedProvince={selectedProvince}
            provinceOptions={provinceOptions}
            municipalityOptions={municipalityOptions}
          />
        </MainContainer>
      </Col>
    </Wrapper>
  );
};

export default AddEndorsementComponent;
